import { useState, useMemo, useCallback, useRef, useEffect, forwardRef, useImperativeHandle } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { dashboardConfigApi } from '@/lib/api'
import { WIDGET_COMPONENTS } from './widgets'
import { Button } from '@/components/ui/button'
import { Plus, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { WidgetConfig, WidgetType } from '@/types'
import GridLayout, { type Layout, type LayoutItem } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'

// Row height in pixels - should be at least as tall as the widget min-height (140px) plus margin
const ROW_HEIGHT = 160

export interface DynamicDashboardRef {
  startEditing: () => void
  cancelEditing: () => void
  save: () => void
  reset: () => void
}

interface DynamicDashboardProps {
  onAddWidget?: () => void
  onEditingChange?: (isEditing: boolean) => void
}

export const DynamicDashboard = forwardRef<DynamicDashboardRef, DynamicDashboardProps>(
  function DynamicDashboard({ onAddWidget, onEditingChange }, ref) {
    const { t } = useTranslation()
    const queryClient = useQueryClient()
    const [isEditing, setIsEditing] = useState(false)
    const [localWidgets, setLocalWidgets] = useState<WidgetConfig[] | null>(null)
    const containerRef = useRef<HTMLDivElement>(null)
    const [containerWidth, setContainerWidth] = useState(1200) // Default width

    // Measure container width using ResizeObserver to detect sidebar collapse/expand
    useEffect(() => {
      if (!containerRef.current) return

      const updateWidth = () => {
        if (containerRef.current) {
          const width = containerRef.current.offsetWidth
          if (width > 0) {
            setContainerWidth(width)
          }
        }
      }

      // Use ResizeObserver to detect container size changes (e.g., sidebar collapse)
      const resizeObserver = new ResizeObserver(() => {
        updateWidth()
      })

      resizeObserver.observe(containerRef.current)

      // Initial measurement
      updateWidth()

      return () => {
        resizeObserver.disconnect()
      }
    }, [])

    // Fetch dashboard configuration
    const { data: config, isLoading } = useQuery({
      queryKey: ['dashboard-config'],
      queryFn: dashboardConfigApi.getConfig,
    })

    // Save configuration mutation
    const saveMutation = useMutation({
      mutationFn: dashboardConfigApi.saveConfig,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-config'] })
        toast.success(t('dashboard.configSaved'))
        setIsEditing(false)
        setLocalWidgets(null)
      },
      onError: () => {
        toast.error(t('dashboard.configSaveError'))
      },
    })

    // Reset configuration mutation
    const resetMutation = useMutation({
      mutationFn: dashboardConfigApi.resetConfig,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['dashboard-config'] })
        toast.success(t('dashboard.configReset'))
        setIsEditing(false)
        setLocalWidgets(null)
      },
      onError: () => {
        toast.error(t('dashboard.configResetError'))
      },
    })

    // Sync localWidgets with config when config changes (e.g., widget added from dialog)
    // This ensures that widgets added while in edit mode are reflected
    useEffect(() => {
      if (isEditing && config?.widgets) {
        // Only update if config has more widgets than localWidgets (widget was added)
        // or if localWidgets is null (just entered edit mode)
        if (!localWidgets || config.widgets.length > localWidgets.length) {
          setLocalWidgets(config.widgets)
        }
      }
    }, [isEditing, config?.widgets, localWidgets])

    // Use local widgets when editing, otherwise use config
    const widgets = useMemo(() => {
      if (isEditing && localWidgets) {
        return localWidgets
      }
      return config?.widgets || []
    }, [isEditing, localWidgets, config])

    // Filter visible widgets
    const visibleWidgets = useMemo(() => {
      return widgets.filter(w => w.visible)
    }, [widgets])

    // Convert widgets to react-grid-layout format
    const layout = useMemo((): Layout => {
      return visibleWidgets.map(w => ({
        i: w.id,
        x: w.position.x,
        y: w.position.y,
        w: w.size.width,
        h: w.size.height,
        minW: 2,
        minH: 1,
        maxW: 12,
        maxH: 4,
        static: !isEditing, // Make items static (non-draggable/resizable) when not editing
      })) as Layout
    }, [visibleWidgets, isEditing])

    const handleStartEditing = useCallback(() => {
      setLocalWidgets([...widgets])
      setIsEditing(true)
      onEditingChange?.(true)
    }, [widgets, onEditingChange])

    const handleCancelEditing = useCallback(() => {
      setLocalWidgets(null)
      setIsEditing(false)
      onEditingChange?.(false)
    }, [onEditingChange])

    const handleSave = useCallback(() => {
      if (localWidgets) {
        saveMutation.mutate({ widgets: localWidgets })
        onEditingChange?.(false)
      }
    }, [localWidgets, saveMutation, onEditingChange])

    const handleRemoveWidget = (widgetId: string) => {
      if (localWidgets) {
        setLocalWidgets(localWidgets.map(w =>
          w.id === widgetId ? { ...w, visible: false } : w
        ))
      }
    }

    const handleReset = useCallback(() => {
      if (confirm(t('dashboard.confirmReset'))) {
        resetMutation.mutate()
        onEditingChange?.(false)
      }
    }, [t, resetMutation, onEditingChange])

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      startEditing: handleStartEditing,
      cancelEditing: handleCancelEditing,
      save: handleSave,
      reset: handleReset,
    }), [handleStartEditing, handleCancelEditing, handleSave, handleReset])

    // Handle layout changes from drag/resize
    const handleLayoutChange = useCallback((newLayout: Layout) => {
      if (!isEditing || !localWidgets) return

      const updatedWidgets = localWidgets.map(widget => {
        const layoutItem = newLayout.find((l: LayoutItem) => l.i === widget.id)
        if (layoutItem) {
          return {
            ...widget,
            position: { x: layoutItem.x, y: layoutItem.y },
            size: { width: layoutItem.w, height: layoutItem.h },
          }
        }
        return widget
      })

      setLocalWidgets(updatedWidgets)
    }, [isEditing, localWidgets])

    if (isLoading) {
      return (
        <div ref={containerRef} className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      )
    }

    return (
      <div ref={containerRef} className="h-full flex flex-col">
        {/* Widget Grid */}
        {visibleWidgets.length > 0 ? (
          <div className="overflow-x-auto flex-1">
            <GridLayout
              className={`layout ${isEditing ? 'editing' : ''}`}
              layout={layout}
              width={containerWidth}
              autoSize={true}
              gridConfig={{
                cols: 12,
                rowHeight: ROW_HEIGHT,
                margin: [16, 16],
                containerPadding: [0, 0],
              }}
              dragConfig={{
                enabled: isEditing,
                handle: '.widget-drag-handle',
              }}
              resizeConfig={{
                enabled: isEditing,
              }}
              onLayoutChange={handleLayoutChange}
            >
              {visibleWidgets.map((widget) => {
                const WidgetComponent = WIDGET_COMPONENTS[widget.type as WidgetType]
                if (!WidgetComponent) return null

                return (
                  <div
                    key={widget.id}
                    className={`relative ${isEditing ? 'ring-2 ring-primary/20 rounded-lg cursor-move' : ''}`}
                  >
                    {isEditing && (
                      <>
                        <div className="widget-drag-handle absolute top-0 left-0 right-0 h-8 cursor-grab active:cursor-grabbing z-10" />
                        <Button
                          variant="destructive"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6 z-20"
                          onClick={() => handleRemoveWidget(widget.id)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                    <div className="h-full">
                      <WidgetComponent />
                    </div>
                  </div>
                )
              })}
            </GridLayout>
          </div>
        ) : (
          <div className="text-center py-12 text-muted-foreground">
            <p>{t('dashboard.noWidgets')}</p>
            <Button variant="outline" className="mt-4" onClick={onAddWidget}>
              <Plus className="h-4 w-4 mr-1" />
              {t('dashboard.addWidget')}
            </Button>
          </div>
        )}
      </div>
    )
  })

